import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from "recharts";
import {
    AlertTriangle,
    ArrowDownRight,
    ArrowUpRight,
    Boxes,
    CalendarClock,
    CheckCircle2,
    ClipboardList,
    HardHat,
    Landmark,
    PlayCircle,
    Power,
    Receipt,
    Sparkles,
    TrendingUp,
    Users,
    Wallet,
    Wrench,
} from "lucide-react";

import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { dashboardService } from "@/services/dashboard.service";
import { financeService } from "@/services/finance.service";
import { formatBRL, formatDate } from "@/lib/utils";
import { EmptyState } from "@/components/common/EmptyState";
import { WO_STATUS_LABELS, WO_STATUSES } from "@/config/constants";
import { usePermission } from "@/hooks/usePermission";
import {
    FinanceStatusBadge,
} from "@/features/finance/components/FinanceStatusBadge";

const CHART_COLORS = [
    "hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
    "hsl(var(--chart-4))", "hsl(var(--chart-5))", "hsl(var(--chart-6))",
];

const FINANCE_UPCOMING_LIMIT = 5;

export default function DashboardPage() {
    const navigate = useNavigate();
    const { can } = usePermission();
    const canSeeFinance = can("finance", "read");

    const stats = useQuery({ queryKey: ["dashboard", "stats"], queryFn: dashboardService.stats });
    const recent = useQuery({ queryKey: ["dashboard", "recent"], queryFn: dashboardService.recent });
    const chartWOs = useQuery({
        queryKey: ["dashboard", "work-orders-by-status"],
        queryFn: dashboardService.workOrdersByStatus,
    });
    const financeDashboard = useQuery({
        queryKey: ["finance-dashboard"],
        queryFn: () => financeService.dashboard(),
        enabled: canSeeFinance,
        refetchInterval: 60000,
    });
    const upcomingReceivables = useQuery({
        queryKey: ["dashboard-upcoming-receivables"],
        queryFn: () => financeService.listAccounts({
            type: "receivable",
            pageSize: FINANCE_UPCOMING_LIMIT,
            sortBy: "created_at",
            sortOrder: "desc",
            status: "em_aberto",
        }),
        enabled: canSeeFinance,
    });

    const financeKpis = financeDashboard.data?.kpis || {};
    const hasOverdue =
        (financeKpis.overdue_receivable_count || 0) + (financeKpis.overdue_payable_count || 0) > 0;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Painel de Controle"
                description="Visão geral do negócio: OS, equipamentos, financeiro e clientes."
            />

            {/* OS */}
            <section aria-labelledby="dashboard-os-heading">
                <div id="dashboard-os-heading" className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <ClipboardList className="h-3 w-3" />
                    Ordens de Serviço
                </div>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <StatCard label="OS Novas"
                        value={stats.data?.os_novas ?? "—"}
                        icon={Sparkles} loading={stats.isLoading}
                        testId="stat-os-novas"
                        hint="aguardando aceite" />
                    <StatCard label="OS Em Atendimento"
                        value={stats.data?.os_em_atendimento ?? "—"}
                        icon={PlayCircle} loading={stats.isLoading}
                        testId="stat-os-em-atendimento"
                        hint={`${stats.data?.os_aceitas ?? 0} aceitas`} />
                    <StatCard label="OS Finalizadas"
                        value={stats.data?.os_finalizadas ?? "—"}
                        icon={CheckCircle2} loading={stats.isLoading}
                        testId="stat-os-finalizadas" />
                    <StatCard label="Técnicos Disponíveis"
                        value={stats.data?.tecnicos_disponiveis ?? "—"}
                        icon={HardHat} loading={stats.isLoading}
                        testId="stat-tecnicos-disponiveis"
                        hint={`${stats.data?.total_technicians ?? 0} no total`} />
                </div>
            </section>

            {/* Financeiro (Admin-only) */}
            {canSeeFinance && (
                <section aria-labelledby="dashboard-finance-heading" data-testid="dashboard-finance-section">
                    <div id="dashboard-finance-heading" className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Wallet className="h-3 w-3" />
                        Financeiro
                    </div>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                        <StatCard label="Saldo a Receber"
                            value={formatBRL(financeKpis.total_receivable || 0)}
                            icon={Landmark} loading={financeDashboard.isLoading}
                            testId="stat-finance-total-receivable"
                            hint={`Recebido no mês: ${formatBRL(financeKpis.received_this_month || 0)}`} />
                        <StatCard label="Saldo a Pagar"
                            value={formatBRL(financeKpis.total_payable || 0)}
                            icon={Receipt} loading={financeDashboard.isLoading}
                            testId="stat-finance-total-payable"
                            hint={`Pago no mês: ${formatBRL(financeKpis.paid_this_month || 0)}`} />
                        <StatCard label="Parcelas Vencidas"
                            value={
                                (financeKpis.overdue_receivable_count || 0) +
                                (financeKpis.overdue_payable_count || 0)
                            }
                            icon={AlertTriangle} loading={financeDashboard.isLoading}
                            testId="stat-finance-overdue"
                            hint={`${formatBRL((financeKpis.overdue_receivable_amount || 0) + (financeKpis.overdue_payable_amount || 0))} em atraso`} />
                        <StatCard label="Próx. 30 dias (líquido)"
                            value={formatBRL(
                                (financeKpis.next30_receivable || 0) - (financeKpis.next30_payable || 0),
                            )}
                            icon={CalendarClock} loading={financeDashboard.isLoading}
                            testId="stat-finance-next30"
                            hint={`+${formatBRL(financeKpis.next30_receivable || 0)} / -${formatBRL(financeKpis.next30_payable || 0)}`} />
                    </div>
                </section>
            )}

            {/* Equipamentos & clientes */}
            <section aria-labelledby="dashboard-assets-heading">
                <div id="dashboard-assets-heading" className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <Boxes className="h-3 w-3" />
                    Equipamentos & Clientes
                </div>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    <StatCard label="Equipamentos Cadastrados"
                        value={stats.data?.equipamentos_cadastrados ?? "—"}
                        icon={Boxes} loading={stats.isLoading}
                        testId="stat-eq-cadastrados" />
                    <StatCard label="Em Atendimento"
                        value={stats.data?.equipamentos_em_atendimento ?? "—"}
                        icon={Wrench} loading={stats.isLoading}
                        testId="stat-eq-em-atendimento"
                        hint="OS em andamento" />
                    <StatCard label="Equipamentos Parados"
                        value={stats.data?.equipamentos_parados ?? "—"}
                        icon={Power} loading={stats.isLoading}
                        testId="stat-eq-parados" />
                    <StatCard label="Total de Clientes"
                        value={stats.data?.total_customers ?? "—"}
                        icon={Users} loading={stats.isLoading}
                        testId="stat-total-clientes" />
                </div>
            </section>

            {/* Overdue alert banner */}
            {canSeeFinance && hasOverdue && (
                <button
                    type="button"
                    onClick={() => navigate("/finance")}
                    className="flex w-full items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left transition-colors hover:bg-red-100 dark:border-red-500/40 dark:bg-red-500/10 dark:hover:bg-red-500/15"
                    data-testid="dashboard-overdue-banner"
                >
                    <div className="grid h-9 w-9 place-items-center rounded-md bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300">
                        <AlertTriangle className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                        <div className="text-sm font-medium text-red-900 dark:text-red-200">
                            {(financeKpis.overdue_receivable_count || 0) + (financeKpis.overdue_payable_count || 0)} parcela(s) em atraso
                        </div>
                        <div className="text-xs text-red-800/80 dark:text-red-300/80">
                            {formatBRL((financeKpis.overdue_receivable_amount || 0) + (financeKpis.overdue_payable_amount || 0))} pendentes · toque para ver no Financeiro
                        </div>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-red-700 dark:text-red-300" />
                </button>
            )}

            {/* Gráfico OS + Últimas OS */}
            <div className="grid gap-6 lg:grid-cols-12">
                <Card className="rounded-xl border shadow-sm lg:col-span-8 min-w-0" data-testid="chart-work-orders-by-status">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Ordens de Serviço por Status</CardTitle>
                    </CardHeader>
                    <CardContent className="h-72">
                        {chartWOs.isLoading ? (
                            <Skeleton className="h-full w-full" />
                        ) : (chartWOs.data || []).length === 0 ? (
                            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                                Sem ordens de serviço.
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={WO_STATUSES.map((s) => {
                                        const row = (chartWOs.data || []).find((r) => r.status === s.value);
                                        return { label: s.label, count: row?.count || 0 };
                                    })}
                                    margin={{ top: 8, right: 12, left: -12, bottom: 4 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                                    <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                                    <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                                    <RTooltip cursor={{ fill: "hsl(var(--muted) / 0.5)" }}
                                        contentStyle={{
                                            background: "hsl(var(--popover))",
                                            border: "1px solid hsl(var(--border))",
                                            borderRadius: 8, fontSize: 12,
                                            color: "hsl(var(--popover-foreground))",
                                        }} />
                                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                        {WO_STATUSES.map((status, i) => (
                                            <Cell key={`wo-status-cell-${status.value}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>

                <Card className="rounded-xl border shadow-sm lg:col-span-4 min-w-0">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">OS recentes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {recent.isLoading ? (
                            <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={`sk-recent-wo-${i}`} className="h-10 w-full" />)}</div>
                        ) : (recent.data?.work_orders || []).length === 0 ? (
                            <EmptyState icon={ClipboardList}
                                title="Nenhuma OS ainda"
                                description="Crie sua primeira ordem de serviço."
                                actionLabel="Nova OS"
                                onAction={() => navigate("/work-orders/new")}
                                testId="empty-recent-wos" />
                        ) : (
                            <ul className="divide-y" data-testid="recent-wos-list">
                                {(recent.data?.work_orders || []).map((w) => (
                                    <li key={w.id}
                                        onClick={() => navigate(`/work-orders/${w.id}`)}
                                        className="flex cursor-pointer items-center justify-between gap-3 py-3 hover:bg-accent/40"
                                        data-testid={`recent-wo-${w.id}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                                                <ClipboardList className="h-4 w-4" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-mono text-sm font-medium">{w.number}</div>
                                                <div className="truncate text-xs text-muted-foreground" title={w.title}>{w.title || "—"}</div>
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="text-[10px]">
                                            {WO_STATUS_LABELS[w.status] || w.status}
                                        </Badge>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Financeiro recente + Últimos equipamentos */}
            <div className="grid gap-6 lg:grid-cols-12">
                {canSeeFinance && (
                    <Card className="rounded-xl border shadow-sm lg:col-span-6 min-w-0" data-testid="dashboard-upcoming-receivables">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <ArrowDownRight className="h-4 w-4 text-emerald-600" />
                                Próximos recebimentos
                            </CardTitle>
                            <button
                                type="button"
                                onClick={() => navigate("/finance/receivables")}
                                className="text-xs text-muted-foreground hover:text-foreground"
                                data-testid="dashboard-goto-receivables"
                            >
                                Ver todos →
                            </button>
                        </CardHeader>
                        <CardContent>
                            {upcomingReceivables.isLoading ? (
                                <div className="space-y-3">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <Skeleton key={`sk-recv-${i}`} className="h-10 w-full" />
                                    ))}
                                </div>
                            ) : (upcomingReceivables.data?.items || []).length === 0 ? (
                                <EmptyState icon={TrendingUp}
                                    title="Nada a receber no momento"
                                    description="Aprove uma proposta ou crie uma conta manualmente."
                                    actionLabel="Criar conta"
                                    onAction={() => navigate("/finance/receivables")} />
                            ) : (
                                <ul className="divide-y">
                                    {(upcomingReceivables.data?.items || []).map((acc) => {
                                        const nextInst = (acc.installments || []).find(
                                            (i) => i.status !== "paga" && i.status !== "cancelada",
                                        );
                                        return (
                                            <li
                                                key={acc.id}
                                                onClick={() => navigate(`/finance/receivables/${acc.id}`)}
                                                className="flex cursor-pointer items-center justify-between gap-3 py-3 hover:bg-accent/40"
                                                data-testid={`upcoming-receivable-${acc.number}`}
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                                                        <ArrowDownRight className="h-4 w-4" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-mono text-sm font-medium">{acc.number}</div>
                                                        <div className="truncate text-xs text-muted-foreground">
                                                            {acc.counterparty_name || "—"}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm font-medium">
                                                        {formatBRL(acc.balance || 0)}
                                                    </div>
                                                    <div className="text-[10px] text-muted-foreground">
                                                        {nextInst ? `Venc. ${formatDate(nextInst.due_date)}` : "Quitada"}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                )}

                <Card className={`rounded-xl border shadow-sm min-w-0 ${canSeeFinance ? "lg:col-span-6" : "lg:col-span-12"}`}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">
                            Últimos equipamentos atendidos
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {recent.isLoading ? (
                            <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={`sk-sa-${i}`} className="h-10 w-full" />)}</div>
                        ) : (recent.data?.serviced_assets || []).length === 0 ? (
                            <EmptyState icon={Boxes}
                                title="Nenhum equipamento atendido"
                                description="As OS ainda não têm equipamento vinculado."
                                actionLabel="Cadastrar equipamento"
                                onAction={() => navigate("/assets/new")} />
                        ) : (
                            <ul className="divide-y" data-testid="recent-serviced-assets">
                                {(recent.data?.serviced_assets || []).map((row) => (
                                    <li key={row.asset?.id}
                                        onClick={() => navigate(`/assets/${row.asset.id}`)}
                                        className="flex cursor-pointer items-center justify-between gap-3 py-3 hover:bg-accent/40"
                                        data-testid={`recent-serviced-${row.asset?.internal_code}`}>
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                                                <Boxes className="h-4 w-4" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-mono text-sm font-medium">{row.asset?.internal_code}</div>
                                                <div className="truncate text-xs text-muted-foreground">
                                                    {row.asset?.category} {row.asset?.manufacturer ? `· ${row.asset.manufacturer}` : ""}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[11px] font-mono text-muted-foreground">{row.last_wo_number}</div>
                                            <div className="text-[10px] text-muted-foreground">
                                                {row.last_wo_at ? formatDate(row.last_wo_at) : "—"}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Clientes recentes */}
            <Card className="rounded-xl border shadow-sm min-w-0">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Clientes recentes</CardTitle>
                </CardHeader>
                <CardContent>
                    {recent.isLoading ? (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={`sk-c-${i}`} className="h-14 w-full" />)}</div>
                    ) : (recent.data?.customers || []).length === 0 ? (
                        <EmptyState icon={Users}
                            title="Nenhum cliente cadastrado"
                            description="Adicione seu primeiro cliente."
                            actionLabel="Novo cliente"
                            onAction={() => navigate("/customers/new")}
                            testId="empty-recent-customers" />
                    ) : (
                        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="recent-customers-list">
                            {(recent.data?.customers || []).map((c) => (
                                <li key={c.id}
                                    onClick={() => navigate(`/customers/${c.id}`)}
                                    className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-transparent p-3 hover:border-border hover:bg-accent/40"
                                    data-testid={`recent-customer-${c.id}`}>
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                                            <Users className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="truncate text-sm font-medium">{c.legal_name}</div>
                                            <div className="truncate text-xs text-muted-foreground">
                                                {[c.trade_name, c.city].filter(Boolean).join(" · ") || "—"}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="text-[10px] shrink-0">{formatDate(c.created_at)}</Badge>
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
